/**
 * Settlement journal for the Solana app: what was intended, what was sent
 * and what still has to be delivered, encrypted at rest under a key derived
 * from the account's spending key so a shared browser profile learns
 * nothing. A record exists before anything leaves the browser and is
 * completed only once every output note is spendable and delivered, which
 * is what lets a reload or a crash be reconciled from chain state.
 */
import type { SolanaSettlementIntent } from "@stealthos/sdk";
import type { JoinSplitNote } from "../../joinsplit/types";
import type { FlatJoinSplitV2EncryptedNoteEnvelope } from "../pendingJournal";
import { fail } from "./common";

const JOURNAL_SCHEMA_VERSION = 1 as const;
const STORAGE_PREFIX = "stealthos_solana_joinsplit_v2_journal_v1";
const BIGINT_PREFIX = "__stealthos_bigint:";
const SOLANA_JOURNAL_MAX_ACTIVE_RECORDS = 64;

export type SolanaJournalKind =
  | "deposit"
  | "deposit_merge"
  | "joinsplit"
  | "withdraw"
  | "withdraw_batch"
  | "swap"
  | "migrate";

export type SolanaJournalState =
  | "prepared"
  | "broadcast"
  | "finalized"
  | "delivery_pending"
  | "completed"
  | "watching"
  | "expired"
  | "failed";

const SOLANA_JOURNAL_ACTIVE_STATES: readonly SolanaJournalState[] = [
  "prepared",
  "broadcast",
  "finalized",
  "delivery_pending",
  "watching",
];

export interface SolanaJournalDelivery {
  commitment: string;
  recipientMpkHex: string;
  recipientInboxPublicKeyHex?: string;
  keepLocal: boolean;
  encryptedNote?: FlatJoinSplitV2EncryptedNoteEnvelope;
  disposition?: "delivered" | "superseded";
  completedAt?: number;
}

export interface SolanaJournalRecord {
  schemaVersion: typeof JOURNAL_SCHEMA_VERSION;
  id: string;
  kind: SolanaJournalKind;
  state: SolanaJournalState;
  network: string;
  programId: string;
  account: string;
  /** Relayed kinds: the exact intent posted, for idempotent resubmission. */
  intent?: SolanaSettlementIntent;
  /** Encrypted before submission; the amount is learned from the finalized receipt. */
  swapSeed?: {
    random: bigint;
    recipientMpk: bigint;
    outputMint: string | null;
  };
  /** Deposits: what the wallet signed, for reconciliation from chain. */
  deposit?: {
    commitment: string;
    mint: string | null;
    amount: string;
    treeIndex: number;
    /** The only blockhash this deposit attempt may ask the wallet to sign. */
    transactionWindow?: {
      blockhash: string;
      lastValidBlockHeight: number;
      slot: number;
    };
    /** Present only for atomic funded deposits that also consume one UTXO. */
    merge?: {
      sourceNullifier: string;
      sourceCommitment: string;
      merkleRoot: string;
      deadlineSlot: string;
      nonce: string;
    };
  };
  sourceCommitments: string[];
  sourceNullifiers: string[];
  outputNotes: JoinSplitNote[];
  deliveries: SolanaJournalDelivery[];
  signature?: string;
  lastValidBlockHeight?: number;
  slot?: number;
  error?: string;
  /** Absent on older records that incorrectly treated network errors as final. */
  failureConfirmed?: boolean;
  /** Set before contacting the relayer; absent from the pre-submission backup. */
  submissionAttempted?: boolean;
  /** Remote restoration requires a fresh, explicit user action before replay. */
  restoredFromBackup?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface SolanaJournalScope {
  network: string;
  programId: string;
  account: string;
  spendingKey: Uint8Array;
}

interface CanonicalScope extends SolanaJournalScope {
  storageKey: string;
}

export interface SolanaJournalEnvelope {
  schemaVersion: typeof JOURNAL_SCHEMA_VERSION;
  iv: string;
  ciphertext: string;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function replacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? `${BIGINT_PREFIX}${value}` : value;
}

function reviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && value.startsWith(BIGINT_PREFIX)) {
    return BigInt(value.slice(BIGINT_PREFIX.length));
  }
  return value;
}

/** Includes secret note randomness and delivery destinations, not just commitments. */
export function solanaJournalIntentFingerprint(
  record: SolanaJournalRecord,
): string {
  return JSON.stringify(
    {
      id: record.id,
      createdAt: record.createdAt,
      kind: record.kind,
      network: record.network,
      programId: record.programId,
      account: record.account,
      intent: record.intent,
      deposit: record.deposit,
      swapSeed: record.swapSeed,
      sourceCommitments: record.sourceCommitments,
      sourceNullifiers: record.sourceNullifiers,
      outputNotes: record.outputNotes,
      deliveries: record.deliveries,
    },
    replacer,
  );
}

function canonicalScope(scope: SolanaJournalScope): CanonicalScope {
  if (!scope.network || !scope.programId || !scope.account) {
    fail("journal scope is incomplete");
  }
  if (
    !(scope.spendingKey instanceof Uint8Array) ||
    scope.spendingKey.length !== 32
  ) {
    fail("journal scope requires the 32-byte spending key");
  }
  return {
    ...scope,
    storageKey: `${STORAGE_PREFIX}:${scope.network}:${scope.programId}:${scope.account}`,
  };
}

function requireStorage(): Storage {
  if (
    typeof window === "undefined" ||
    !window.localStorage ||
    !globalThis.crypto?.subtle
  ) {
    fail("the settlement journal needs localStorage and WebCrypto");
  }
  return window.localStorage;
}

const keyCache = new WeakMap<Uint8Array, Map<string, Promise<CryptoKey>>>();

async function deriveKey(scope: CanonicalScope): Promise<CryptoKey> {
  let perSpendingKey = keyCache.get(scope.spendingKey);
  if (!perSpendingKey) {
    perSpendingKey = new Map();
    keyCache.set(scope.spendingKey, perSpendingKey);
  }
  let pending = perSpendingKey.get(scope.storageKey);
  if (!pending) {
    pending = (async () => {
      const label = new TextEncoder().encode(
        `${STORAGE_PREFIX}:${scope.storageKey}:`,
      );
      const material = new Uint8Array(label.length + scope.spendingKey.length);
      material.set(label, 0);
      material.set(scope.spendingKey, label.length);
      const digest = await globalThis.crypto.subtle.digest("SHA-256", material);
      return globalThis.crypto.subtle.importKey(
        "raw",
        digest,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"],
      );
    })();
    perSpendingKey.set(scope.storageKey, pending);
  }
  return pending;
}

function recordKey(scope: CanonicalScope, id: string): string {
  return `${scope.storageKey}:record:${id}`;
}

async function writeRecord(
  scope: CanonicalScope,
  record: SolanaJournalRecord,
): Promise<void> {
  const storage = requireStorage();
  const key = await deriveKey(scope);
  const storageKey = recordKey(scope, record.id);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(record, replacer));
  const ciphertext = await globalThis.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(storageKey),
    },
    key,
    plaintext,
  );
  const envelope: SolanaJournalEnvelope = {
    schemaVersion: JOURNAL_SCHEMA_VERSION,
    iv: encodeBase64(iv),
    ciphertext: encodeBase64(new Uint8Array(ciphertext)),
  };
  const encoded = JSON.stringify(envelope);
  storage.setItem(storageKey, encoded);
  if (storage.getItem(storageKey) !== encoded) {
    fail("settlement journal write did not persist");
  }
}

async function readRecord(
  scope: CanonicalScope,
  storageKey: string,
): Promise<SolanaJournalRecord | null> {
  const raw = requireStorage().getItem(storageKey);
  if (!raw) return null;
  return decryptRecord(scope, storageKey, raw);
}

async function decryptRecord(
  scope: CanonicalScope,
  storageKey: string,
  raw: string,
): Promise<SolanaJournalRecord> {
  let envelope: SolanaJournalEnvelope;
  try {
    envelope = JSON.parse(raw);
  } catch {
    fail("settlement journal record is corrupt");
  }
  if (envelope.schemaVersion !== JOURNAL_SCHEMA_VERSION) {
    fail("settlement journal record has an unsupported schema");
  }
  const key = await deriveKey(scope);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await globalThis.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: decodeBase64(envelope.iv),
        additionalData: new TextEncoder().encode(storageKey),
      },
      key,
      decodeBase64(envelope.ciphertext),
    );
  } catch {
    fail(
      "settlement journal record could not be decrypted with this account's key",
    );
  }
  const record = JSON.parse(new TextDecoder().decode(plaintext), reviver);
  if (
    record?.schemaVersion !== JOURNAL_SCHEMA_VERSION ||
    typeof record.id !== "string"
  ) {
    fail("settlement journal record is malformed");
  }
  return record as SolanaJournalRecord;
}

const locks = new Map<string, Promise<unknown>>();

async function withScopeLock<T>(
  scope: CanonicalScope,
  run: () => Promise<T>,
): Promise<T> {
  const previous = locks.get(scope.storageKey) ?? Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(async (): Promise<T> => {
      const browserLocks = globalThis.navigator?.locks;
      return browserLocks
        ? await browserLocks.request(
            `${scope.storageKey}:write`,
            { mode: "exclusive" },
            run,
          )
        : await run();
    });
  locks.set(scope.storageKey, next);
  try {
    return await next;
  } finally {
    if (locks.get(scope.storageKey) === next) locks.delete(scope.storageKey);
  }
}

function recordKeysOf(scope: CanonicalScope): string[] {
  const storage = requireStorage();
  const prefix = `${scope.storageKey}:record:`;
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && key.startsWith(prefix)) keys.push(key);
  }
  return keys;
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

/** Deterministic id: a nullifier set can only ever settle once on one pool. */
export async function solanaSettlementJournalId(
  network: string,
  programId: string,
  nullifiers: readonly string[],
): Promise<string> {
  if (nullifiers.length === 0) fail("a settlement spends at least one note");
  const sorted = nullifiers.map((value) => value.toLowerCase()).sort();
  return sha256Hex(`settlement:${network}:${programId}:${sorted.join(",")}`);
}

export async function solanaDepositJournalId(
  network: string,
  programId: string,
  commitment: string,
): Promise<string> {
  return sha256Hex(
    `deposit:${network}:${programId}:${commitment.toLowerCase()}`,
  );
}

export async function listSolanaJournal(
  scope: SolanaJournalScope,
  options?: {
    skipUnreadable?: boolean;
    onUnreadable?: (count: number) => void;
  },
): Promise<SolanaJournalRecord[]> {
  const canonical = canonicalScope(scope);
  const records: SolanaJournalRecord[] = [];
  let unreadable = 0;
  for (const key of recordKeysOf(canonical)) {
    try {
      const record = await readRecord(canonical, key);
      if (record) records.push(record);
    } catch (error) {
      if (!options?.skipUnreadable) throw error;
      unreadable += 1;
    }
  }
  if (unreadable > 0) options?.onUnreadable?.(unreadable);
  return records.sort((left, right) => left.createdAt - right.createdAt);
}

export async function getSolanaJournalRecord(
  scope: SolanaJournalScope,
  id: string,
): Promise<SolanaJournalRecord | null> {
  const canonical = canonicalScope(scope);
  return readRecord(canonical, recordKey(canonical, id));
}

function canonicalBase64(value: unknown): value is string {
  if (typeof value !== "string" || value.length % 4 !== 0) return false;
  try {
    return encodeBase64(decodeBase64(value)) === value;
  } catch {
    return false;
  }
}

function canBackUpForRecovery(record: SolanaJournalRecord): boolean {
  return (
    record.state === "prepared" ||
    record.state === "broadcast" ||
    (record.state === "failed" && record.failureConfirmed !== true)
  );
}

/** Export the ciphertext for the current recoverable version of an intent. */
export async function exportSolanaJournalEnvelope(
  scope: SolanaJournalScope,
  id: string,
): Promise<{ envelope: SolanaJournalEnvelope; record: SolanaJournalRecord }> {
  const canonical = canonicalScope(scope);
  const key = recordKey(canonical, id);
  const raw = requireStorage().getItem(key);
  if (!raw) fail("prepared journal ciphertext is missing");
  const record = await decryptRecord(canonical, key, raw);
  if (!record || !canBackUpForRecovery(record)) {
    fail("only a recoverable journal record may be backed up");
  }
  return {
    envelope: JSON.parse(raw) as SolanaJournalEnvelope,
    record,
  };
}

/** Authenticate a remote snapshot without trusting its chain-state metadata. */
export async function validateSolanaJournalEnvelope(
  scope: SolanaJournalScope,
  id: string,
  envelope: SolanaJournalEnvelope,
): Promise<SolanaJournalRecord> {
  const canonical = canonicalScope(scope);
  if (
    envelope?.schemaVersion !== JOURNAL_SCHEMA_VERSION ||
    !canonicalBase64(envelope.iv) ||
    decodeBase64(envelope.iv).length !== 12 ||
    !canonicalBase64(envelope.ciphertext) ||
    decodeBase64(envelope.ciphertext).length < 16
  ) {
    fail("recovery backup envelope is malformed");
  }
  const raw = JSON.stringify(envelope);
  if (raw.length > 360 * 1024) fail("recovery backup exceeds size limit");
  const record = await decryptRecord(canonical, recordKey(canonical, id), raw);
  if (
    record.id !== id ||
    record.network !== scope.network ||
    record.programId !== scope.programId ||
    record.account !== scope.account ||
    !canBackUpForRecovery(record) ||
    record.slot !== undefined ||
    !Number.isSafeInteger(record.createdAt) ||
    !Array.isArray(record.sourceCommitments) ||
    !Array.isArray(record.sourceNullifiers) ||
    !Array.isArray(record.outputNotes) ||
    !Array.isArray(record.deliveries) ||
    record.outputNotes.length !== record.deliveries.length ||
    record.outputNotes.some(
      (note, index) =>
        typeof note.commitment !== "string" ||
        note.commitment.toLowerCase() !==
          record.deliveries[index]?.commitment?.toLowerCase(),
    )
  ) {
    fail("recovery backup is not a valid recoverable journal record");
  }
  if (record.kind === "deposit" || record.kind === "deposit_merge") {
    const window = record.deposit?.transactionWindow;
    if (
      window &&
      (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(window.blockhash) ||
        !Number.isSafeInteger(window.lastValidBlockHeight) ||
        window.lastValidBlockHeight < 0 ||
        !Number.isSafeInteger(window.slot) ||
        window.slot < 0)
    )
      fail("recovery backup has an invalid transaction expiry window");
    if (
      !record.deposit ||
      record.outputNotes.length !== 1 ||
      record.deposit.commitment.toLowerCase() !==
        record.outputNotes[0].commitment.toLowerCase()
    ) {
      fail("recovery backup deposit does not match its output note");
    }
    if (
      record.kind === "deposit_merge" &&
      (!record.deposit.merge ||
        record.sourceCommitments.length !== 1 ||
        record.sourceNullifiers.length !== 1 ||
        record.deposit.merge.sourceCommitment.toLowerCase() !==
          record.sourceCommitments[0].toLowerCase() ||
        record.deposit.merge.sourceNullifier.toLowerCase() !==
          record.sourceNullifiers[0].toLowerCase())
    ) {
      fail("recovery backup funded merge does not match its spent note");
    }
  } else if (
    !record.intent ||
    !Array.isArray(record.intent.nullifiers) ||
    !Array.isArray(record.intent.commitments) ||
    record.intent.nullifiers.length !== record.sourceNullifiers.length ||
    record.intent.nullifiers.some(
      (nullifier, index) =>
        nullifier.toLowerCase() !==
        record.sourceNullifiers[index].toLowerCase(),
    ) ||
    record.intent.commitments.length !== record.outputNotes.length ||
    record.intent.commitments.some(
      (commitment, index) =>
        commitment.toLowerCase() !==
        record.outputNotes[index].commitment.toLowerCase(),
    )
  ) {
    fail("recovery backup settlement does not match its outputs");
  }
  return record;
}

export async function importSolanaJournalEnvelope(
  scope: SolanaJournalScope,
  id: string,
  envelope: SolanaJournalEnvelope,
): Promise<boolean> {
  const record = await validateSolanaJournalEnvelope(scope, id, envelope);
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const key = recordKey(canonical, id);
    let existing: SolanaJournalRecord | null = null;
    try {
      existing = await readRecord(canonical, key);
    } catch {
      // A corrupted local copy must not block a separately authenticated
      // off-device ciphertext for the same account and journal identity.
    }
    if (
      existing &&
      !(
        existing.state === "failed" &&
        existing.failureConfirmed === true &&
        existing.createdAt < record.createdAt
      )
    ) {
      // Enrich the same attempt with its durable pre-broadcast signature.
      // Never replace a completed outcome or a different payment version.
      if (
        (existing.state === "prepared" || existing.state === "watching") &&
        !existing.signature &&
        record.state === "broadcast" &&
        record.signature &&
        solanaJournalIntentFingerprint(existing) ===
          solanaJournalIntentFingerprint(record)
      ) {
        await writeRecord(canonical, { ...record, restoredFromBackup: true });
        return true;
      }
      return false;
    }
    // The remote version may have been saved just before a relayer call whose
    // acknowledgement was lost. No remote snapshot authorizes a fresh send.
    await writeRecord(canonical, { ...record, restoredFromBackup: true });
    return true;
  });
}

async function replaceSolanaJournalEnvelope(
  scope: SolanaJournalScope,
  id: string,
  envelope: SolanaJournalEnvelope,
): Promise<SolanaJournalRecord> {
  const record = await validateSolanaJournalEnvelope(scope, id, envelope);
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const existing = await readRecord(canonical, recordKey(canonical, id));
    if (
      !existing ||
      existing.state === "finalized" ||
      existing.state === "delivery_pending" ||
      existing.state === "completed"
    ) {
      fail("cannot replace a finalized Solana journal with a backup variant");
    }
    await writeRecord(canonical, { ...record, restoredFromBackup: true });
    return record;
  });
}

/** Caller has verified this version against finalized chain evidence. */
export async function adoptVerifiedSolanaJournalEnvelope(
  scope: SolanaJournalScope,
  id: string,
  envelope: SolanaJournalEnvelope,
): Promise<SolanaJournalRecord> {
  return replaceSolanaJournalEnvelope(scope, id, envelope);
}

/** Caller has received an explicit user choice for this authenticated intent. */
export async function selectSolanaJournalEnvelopeForSubmission(
  scope: SolanaJournalScope,
  id: string,
  envelope: SolanaJournalEnvelope,
): Promise<SolanaJournalRecord> {
  return replaceSolanaJournalEnvelope(scope, id, envelope);
}

export async function activeSolanaJournalRecords(
  scope: SolanaJournalScope,
  options?: {
    skipUnreadable?: boolean;
    onUnreadable?: (count: number) => void;
  },
): Promise<SolanaJournalRecord[]> {
  return (await listSolanaJournal(scope, options)).filter((record) =>
    isRecoverableRecord(record),
  );
}

export async function solanaJournalReservations(
  scope: SolanaJournalScope,
): Promise<{
  commitments: Set<string>;
  nullifiers: Set<string>;
}> {
  const records = (await activeSolanaJournalRecords(scope)).filter(
    (record) => record.state !== "watching",
  );
  return {
    commitments: new Set(
      records.flatMap((record) =>
        record.sourceCommitments.map((value) => value.toLowerCase()),
      ),
    ),
    nullifiers: new Set(
      records.flatMap((record) =>
        record.sourceNullifiers.map((value) => value.toLowerCase()),
      ),
    ),
  };
}

function isRecoverableRecord(record: SolanaJournalRecord): boolean {
  return (
    SOLANA_JOURNAL_ACTIVE_STATES.includes(record.state) ||
    (record.state === "failed" && record.failureConfirmed !== true)
  );
}

function isFinalizedEmptyWithdrawal(record: SolanaJournalRecord): boolean {
  return (
    (record.state === "finalized" || record.state === "delivery_pending") &&
    (record.kind === "withdraw" || record.kind === "withdraw_batch") &&
    record.intent?.commitments.length === 0 &&
    record.sourceNullifiers.length > 0 &&
    record.outputNotes.length === 0 &&
    record.deliveries.length === 0 &&
    Boolean(record.signature) &&
    Number.isSafeInteger(record.slot) &&
    record.slot! >= 0
  );
}

export type SolanaJournalPrepareInput = Pick<
  SolanaJournalRecord,
  | "id"
  | "kind"
  | "sourceCommitments"
  | "sourceNullifiers"
  | "outputNotes"
  | "deliveries"
> &
  Partial<Pick<SolanaJournalRecord, "intent" | "deposit" | "swapSeed">>;

/**
 * Persist an intent before it is signed or posted. A record for the same id
 * that is still in flight is returned as is: the caller must reconcile it
 * rather than start a second attempt for the same notes.
 */
export async function prepareSolanaJournalRecord(
  scope: SolanaJournalScope,
  input: SolanaJournalPrepareInput,
): Promise<{ record: SolanaJournalRecord; resumed: boolean }> {
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const existing = await readRecord(
      canonical,
      recordKey(canonical, input.id),
    );
    if (
      existing &&
      (isRecoverableRecord(existing) || existing.state === "completed")
    ) {
      return { record: existing, resumed: true };
    }
    const records = await listSolanaJournal(scope);
    // Older finalized full withdrawals had no delivery to trigger completion.
    for (const record of records) {
      if (!isFinalizedEmptyWithdrawal(record)) continue;
      record.state = "completed";
      record.updatedAt = Date.now();
      await writeRecord(canonical, record);
    }
    const active = records.filter(
      (record) =>
        isRecoverableRecord(record) &&
        record.state !== "watching" &&
        record.id !== input.id,
    );
    const nullifiers = new Set(
      input.sourceNullifiers.map((value) => value.toLowerCase()),
    );
    const commitments = new Set(
      input.sourceCommitments.map((value) => value.toLowerCase()),
    );
    if (
      active.some(
        (record) =>
          record.sourceNullifiers.some((value) =>
            nullifiers.has(value.toLowerCase()),
          ) ||
          record.sourceCommitments.some((value) =>
            commitments.has(value.toLowerCase()),
          ),
      )
    ) {
      fail(
        "a selected note is reserved by another pending settlement; sync before retrying",
      );
    }
    if (active.length >= SOLANA_JOURNAL_MAX_ACTIVE_RECORDS) {
      fail(
        `too many settlements await reconciliation (${active.length}); resolve them before starting another`,
      );
    }
    const now = Date.now();
    const record: SolanaJournalRecord = {
      schemaVersion: JOURNAL_SCHEMA_VERSION,
      id: input.id,
      kind: input.kind,
      state: "prepared",
      network: scope.network,
      programId: scope.programId,
      account: scope.account,
      ...(input.intent ? { intent: input.intent } : {}),
      ...(input.deposit ? { deposit: input.deposit } : {}),
      ...(input.swapSeed ? { swapSeed: input.swapSeed } : {}),
      sourceCommitments: input.sourceCommitments.map((value) =>
        value.toLowerCase(),
      ),
      sourceNullifiers: input.sourceNullifiers.map((value) =>
        value.toLowerCase(),
      ),
      outputNotes: input.outputNotes,
      deliveries: input.deliveries,
      createdAt: existing ? Math.max(now, existing.createdAt + 1) : now,
      updatedAt: now,
    };
    await writeRecord(canonical, record);
    return { record, resumed: false };
  });
}

export async function updateSolanaJournalRecord(
  scope: SolanaJournalScope,
  id: string,
  patch: Partial<
    Pick<
      SolanaJournalRecord,
      | "state"
      | "signature"
      | "lastValidBlockHeight"
      | "slot"
      | "error"
      | "failureConfirmed"
      | "submissionAttempted"
      | "outputNotes"
      | "deliveries"
    >
  >,
  expectedIntent?: SolanaJournalRecord,
): Promise<SolanaJournalRecord> {
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const existing = await readRecord(canonical, recordKey(canonical, id));
    if (!existing) fail(`journal record ${id} does not exist`);
    if (
      expectedIntent &&
      solanaJournalIntentFingerprint(existing) !==
        solanaJournalIntentFingerprint(expectedIntent)
    ) {
      fail(
        "journal version changed in another tab before its status could be updated",
      );
    }
    const record: SolanaJournalRecord = {
      ...existing,
      ...patch,
      updatedAt: Date.now(),
    };
    await writeRecord(canonical, record);
    return record;
  });
}

export async function markSolanaJournalDeliveryCompleted(
  scope: SolanaJournalScope,
  id: string,
  commitment: string,
  disposition: "delivered" | "superseded",
  encryptedNote?: FlatJoinSplitV2EncryptedNoteEnvelope,
  expectedIntent?: SolanaJournalRecord,
): Promise<SolanaJournalRecord> {
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const existing = await readRecord(canonical, recordKey(canonical, id));
    if (!existing) fail(`journal record ${id} does not exist`);
    if (
      expectedIntent &&
      solanaJournalIntentFingerprint(existing) !==
        solanaJournalIntentFingerprint(expectedIntent)
    )
      fail("journal version changed before note delivery could be recorded");
    const wanted = commitment.toLowerCase();
    let found = false;
    const deliveries = existing.deliveries.map((delivery) => {
      if (delivery.commitment.toLowerCase() !== wanted) return delivery;
      found = true;
      return {
        ...delivery,
        ...(encryptedNote ? { encryptedNote } : {}),
        disposition,
        completedAt: Date.now(),
      };
    });
    if (!found) fail("journal record has no delivery for that commitment");
    const allDone = deliveries.every((delivery) =>
      Boolean(delivery.disposition),
    );
    const record: SolanaJournalRecord = {
      ...existing,
      deliveries,
      state: allDone ? "completed" : existing.state,
      updatedAt: Date.now(),
    };
    await writeRecord(canonical, record);
    return record;
  });
}

/** Complete a verified, finalized withdrawal that created no output notes. */
export async function completeSolanaJournalWithoutDeliveries(
  scope: SolanaJournalScope,
  id: string,
  expectedIntent?: SolanaJournalRecord,
): Promise<SolanaJournalRecord> {
  const canonical = canonicalScope(scope);
  return withScopeLock(canonical, async () => {
    const existing = await readRecord(canonical, recordKey(canonical, id));
    if (!existing) fail(`journal record ${id} does not exist`);
    if (
      expectedIntent &&
      solanaJournalIntentFingerprint(existing) !==
        solanaJournalIntentFingerprint(expectedIntent)
    )
      fail("journal version changed before withdrawal completion");
    if (existing.state === "completed") return existing;
    if (!isFinalizedEmptyWithdrawal(existing)) {
      fail(
        "only a finalized empty withdrawal can be completed without delivery",
      );
    }
    const record: SolanaJournalRecord = {
      ...existing,
      state: "completed",
      updatedAt: Date.now(),
    };
    await writeRecord(canonical, record);
    return record;
  });
}

export async function removeSolanaJournalRecord(
  scope: SolanaJournalScope,
  id: string,
): Promise<void> {
  const canonical = canonicalScope(scope);
  await withScopeLock(canonical, async () => {
    requireStorage().removeItem(recordKey(canonical, id));
  });
}
